import { message, Upload, UploadProps } from "antd";
import dayjs from "dayjs";
import { DirectUpload } from "@rails/activestorage";
import { isValidPhoneNumber } from "libphonenumber-js";

export const monthFormat = "MMM YYYY";
export const b2c2bDateFormat = "DD MMM YYYY";
export const departureDateFormat = "DD/MM/YYYY";

export const phoneNumberRule = {
  validator: (_: unknown, value: string) => {
    if (!value) return Promise.resolve();
    return isValidPhoneNumber(value)
      ? Promise.resolve()
      : Promise.reject(new Error("Please enter a valid phone number"));
  },
};

export const bookingFormValidationMessages = {
  required: 'This field cannot be empty',
  types: {
    email: '${label} is not a valid email!',
    number: '${label} is not a valid number!',
  },
  pattern: {
    mismatch: 'Incorrect format',
  },
};

// Helper: perform Rails Direct Upload and resolve signed_id
const directUploadToRails = (file: File): Promise<{ signedId: string }> => {
  return new Promise((resolve, reject) => {
    // Use imported DirectUpload or fallback to global
    const DU = DirectUpload || (window as any)?.ActiveStorage?.DirectUpload;
    if (!DU) {
      reject(new Error("Rails Active Storage DirectUpload not available"));
      return;
    }

    const upload = new DU(file, "/rails/active_storage/direct_uploads");
    upload.create((error: Error | null, blob: any) => {
      if (error) {
        reject(error);
      } else {
        resolve({ signedId: blob?.signed_id || blob?.signedId });
      }
    });
  });
};

export const b2c2bUploadProps: UploadProps = {
  name: "file",
  // Limit accepted types to images and PDFs
  accept: ".jpg,.jpeg,.png,.gif,.pdf,.jfif,image/jpeg,image/png,image/gif,application/pdf,image/jfif",
  multiple: false,
  // Use customRequest to perform Rails Direct Upload instead of posting via action
  customRequest: async ({ file, onError, onSuccess }) => {
    try {
      const f = file as File;
      const { signedId } = await directUploadToRails(f);
      // Return signed_id to consumer; antd Upload expects a response object
      onSuccess?.({ signed_id: signedId } as any, f);
    } catch (err) {
      onError?.(err as Error);
    }
  },
  // Prevent default antd auto upload since we handle it via customRequest
  beforeUpload: (file) => {
    // Optional: lightweight type/size check; return true to allow upload and call customRequest.
    const isAllowedType =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/gif" ||
      file.type === "application/pdf" ||
      file.type === "image/jfif";
    const isLt10M = (file.size || 0) / 1024 / 1024 < 10;
    if (!isAllowedType) {
      message.error("You can only upload JPG/PNG/GIF/PDF/JFIF file!");
      return Upload.LIST_IGNORE; // ignore file in list, no upload
    }
    if (!isLt10M) {
      message.error("Image must be smaller than 10MB!");
      return Upload.LIST_IGNORE;
    }
    return true; // proceed, customRequest will be called
  },
};

export const passportExpiryDisabledDates = (current: dayjs.Dayjs, departureDate: string): boolean => {
  if (!departureDate || !current) return false;

  const departure = dayjs(departureDate, departureDateFormat);
  const sixMonthsAfterDeparture = departure.add(6, "months");

  const disabled = current <= sixMonthsAfterDeparture;
  return disabled;
}

export const categoryDobDisabledDate = (
  current: dayjs.Dayjs,
  category: string | undefined,
  departureDate: string | undefined,
  childAgeRanges?: Record<string, { min_age: number; max_age: number }>
): boolean => {
  if (!current) return false;
  if (current.isAfter(dayjs().endOf("day"))) return true;

  const range = category && childAgeRanges?.[category];
  if (!range || !departureDate) return false;

  const departure = dayjs(departureDate, departureDateFormat);
  const oldestAllowedDob = departure.subtract(range.max_age + 1, "year").add(1, "day");
  const youngestAllowedDob = departure.subtract(range.min_age, "year");

  return current.isBefore(oldestAllowedDob) || current.isAfter(youngestAllowedDob);
}